import { createFileRoute } from "@tanstack/react-router";
import { pageMeta } from "@/lib/seo";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@/lib/server-fn-shim";
import { useEffect, useRef, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { getMyTrip } from "@/lib/trip.functions";
import { TripSwitcher } from "@/components/TripSwitcher";
import { getMyPingStats, getMyTrack, upsertMyDiagnostics, getMyMedia } from "@/lib/media.functions";
import { RouteMap, type PhotoMarker } from "@/components/RouteMap";
import { MapErrorBoundary } from "@/components/MapErrorBoundary";
import {
  RoavaPage,
  RoavaCard,
  StatusChip,
  SettingRow,
  PrimaryButton,
  SecondaryButton,
  DangerButton,
  TextButton,
} from "@/components/roava";
import {
  MapPin,
  Play,
  Pause,
  Square,
  Settings as SettingsIcon,
  Check,
  ExternalLink,
  AlertTriangle,
} from "lucide-react";

import { toast } from "sonner";
import { requestLocationPermission, getLocationAuthLevel, isNative } from "@/lib/native";
import { LocationBG, probeLocationBG, drainNativeLocationQueue } from "@/lib/native/location-bg";
import { ManualGpsButton } from "@/components/ManualGpsButton";
import { useGpsStatus } from "@/lib/native/gps-status";
import { openAppSettings } from "@/lib/native/open-app-settings";


import { probePlugin, type PluginProbeResult } from "@/lib/native/plugin-inventory";
import { PhotoScan } from "@/lib/native/photo-scan";
import {
  setTrackingState,
  setTrackingPaused,
  useTrackingState,
  useTrackingPhase,
  useTrackingStartedAt,
} from "@/lib/native/tracking-state";
import { BUILD_ID } from "@/lib/build-info";
import {
  startActiveWatch,
  stopActiveWatch,
  isActiveWatchRunning,
  pauseActiveWatch,
  resumeActiveWatch,
} from "@/lib/native/active-watch";
import { OfflineRouteStatus } from "@/components/OfflineRouteStatus";
import { OfflineTrackingStatus } from "@/components/OfflineTrackingStatus";
import { OfflineMapPanel } from "@/components/OfflineMapPanel";
import { FlightModeTip } from "@/components/FlightModeTip";
import { SyncOutboxCard } from "@/components/SyncOutboxCard";

import { useQueryClient } from "@tanstack/react-query";
import { useCachedTrip } from "@/lib/offline/trip-cache";
import { BatteryGuardCard } from "@/components/permissions/BatteryGuardCard";
import { useBatteryGuard } from "@/lib/native/use-battery-guard";
import { getBatteryGuardMode, getBatteryGuardThresholdPct, getTrackingAllowWhenCharging } from "@/lib/prefs";
import { getBatteryInfo } from "@/lib/native/battery";
import { GpsRateSheet } from "@/components/permissions/GpsRateSheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";


export const Route = createFileRoute("/_authenticated/tracking")({
  head: () => pageMeta({ title: 'Route tracking — Roava by WGP Apps', description: 'Control location sharing so your group can see each other on the trip map.', path: '/tracking' }),
  component: Tracking,
});

function Tracking() {
  const { t } = useTranslation();
  const tripFn = useServerFn(getMyTrip);
  const statsFn = useServerFn(getMyPingStats);
  const trackFn = useServerFn(getMyTrack);
  const { data: tripData } = useQuery({
    queryKey: ["my-trip"],
    queryFn: () => tripFn({}),
    retry: 1,
  });
  const cachedTrip = useCachedTrip();
  const tripId = tripData?.trip?.id ?? cachedTrip?.id ?? null;
  const tripName = tripData?.trip?.name ?? cachedTrip?.name ?? null;
  const { data: stats, isLoading: statsLoading, isError: statsError, refetch } = useQuery({
    queryKey: ["pings", tripId],
    queryFn: () => statsFn({ data: { tripId: tripId! } }),
    enabled: !!tripId,
    refetchInterval: 15_000,
    retry: 2,
  });
  const tracking = useTrackingState();
  const trackingPhase = useTrackingPhase();
  const paused = trackingPhase === "paused";

  const startedAt = useTrackingStartedAt();
  const gps = useGpsStatus();

  const { data: track, refetch: refetchTrack } = useQuery({
    queryKey: ["track", tripId, tracking ? startedAt : null],
    queryFn: () => trackFn({ data: { tripId: tripId!, since: tracking && startedAt ? startedAt : undefined } }),
    enabled: !!tripId,
    refetchInterval: 15_000,
    retry: 2,
  });
  const mediaFn = useServerFn(getMyMedia);
  const { data: mediaData } = useQuery({
    queryKey: ["my-media", tripId],
    queryFn: () => mediaFn({ data: { tripId: tripId! } }),
    enabled: !!tripId,
    refetchInterval: 30_000,
    retry: 2,
  });


  const photoMarkers: PhotoMarker[] = (() => {
    const items = mediaData?.items ?? [];
    const pings = track?.points ?? [];
    const WINDOW_MS = 15 * 60 * 1000;
    const out: PhotoMarker[] = [];
    for (const it of items) {
      if (it.upload_status !== "uploaded" || !it.url) continue;
      const hasGps =
        typeof it.latitude === "number" &&
        typeof it.longitude === "number" &&
        Number.isFinite(it.latitude) &&
        Number.isFinite(it.longitude);
      if (hasGps) {
        out.push({
          id: it.id,
          url: it.url,
          lat: it.latitude,
          lng: it.longitude,
          capturedAt: it.captured_at ?? undefined,
        });
        continue;
      }
      if (!it.captured_at || pings.length === 0) continue;
      const t = new Date(it.captured_at).getTime();
      if (!Number.isFinite(t)) continue;
      let best: { lat: number; lng: number; dt: number } | null = null;
      for (const p of pings) {
        if (!p.capturedAt) continue;
        const pt = new Date(p.capturedAt).getTime();
        if (!Number.isFinite(pt)) continue;
        const dt = Math.abs(pt - t);
        if (dt > WINDOW_MS) continue;
        if (!best || dt < best.dt) best = { lat: p.lat, lng: p.lng, dt };
      }
      if (best) {
        out.push({
          id: it.id,
          url: it.url,
          lat: best.lat,
          lng: best.lng,
          capturedAt: it.captured_at,
        });
      }
    }
    return out;
  })();

  const [authLevel, setAuthLevel] = useState<"always" | "whenInUse" | "denied" | "prompt" | "unknown">("unknown");
  const [pluginStatus, setPluginStatus] = useState<"checking" | "installed" | "missing" | "errored">("checking");
  const [pluginError, setPluginError] = useState<string | null>(null);
  const [inventory, setInventory] = useState<PluginProbeResult[]>([]);
  const [guidance, setGuidance] = useState<"idle" | "open-settings">("idle");
  const [guidanceMessage, setGuidanceMessage] = useState(() => t("tracking.guidance_we_check_back"));
  const [now, setNow] = useState(() => Date.now());
  const [gpsSheet, setGpsSheet] = useState(false);
  const startingRef = useRef(false);
  const qc = useQueryClient();

  const [lowBatteryConfirm, setLowBatteryConfirm] = useState<
    | { pct: number; threshold: number }
    | null
  >(null);

  async function performStart() {
    if (!tripId) { toast.error(t("tracking.toast_join_first")); return; }
    if (startingRef.current) return;
    startingRef.current = true;
    try {
      const perm = await requestLocationPermission();
      if (perm === "denied") { toast.error(t("tracking.toast_perm_denied")); return; }

      const level = await getLocationAuthLevel();
      setAuthLevel(level);

      await setTrackingState(true);
      await startActiveWatch(tripId);
      refetch();
      refetchTrack();
      qc.invalidateQueries({ queryKey: ["trip-dashboard", tripId] });
    } finally {
      startingRef.current = false;
    }
  }

  async function start() {
    const mode = getBatteryGuardMode();
    const threshold = getBatteryGuardThresholdPct();
    const allowWhenCharging = getTrackingAllowWhenCharging();
    console.info(`[battery-guard] upload rule ignored for tracking decision`);
    if (mode !== "off") {
      try {
        const info = await getBatteryInfo();
        if (info.level !== null) {
          const pct = Math.round(info.level * 100);
          const charging = info.isCharging === true;
          const lowAndNoBypass = pct <= threshold && !(charging && allowWhenCharging);
          if (lowAndNoBypass && mode === "pause") {
            console.info(
              `[battery-guard] tracking start battery=${pct} threshold=${threshold} charging=${charging} allowWhenCharging=${allowWhenCharging} decision=block`,
            );
            setLowBatteryConfirm({ pct, threshold });
            return;
          }
          console.info(
            `[battery-guard] tracking start battery=${pct} threshold=${threshold} charging=${charging} allowWhenCharging=${allowWhenCharging} decision=allow`,
          );
        } else {
          console.info(
            `[battery-guard] tracking start battery=unknown threshold=${threshold} charging=${info.isCharging ?? "unknown"} allowWhenCharging=${allowWhenCharging} decision=allow`,
          );
        }
      } catch { /* fall through and start */ }
    } else {
      console.info(
        `[battery-guard] tracking start battery=unchecked threshold=${threshold} charging=unchecked allowWhenCharging=${allowWhenCharging} decision=allow (mode=off)`,
      );
    }
    await performStart();
  }

  async function stop() {
    await setTrackingState(false);
    await stopActiveWatch({ source: "tracking-screen-stop" });
    qc.invalidateQueries({ queryKey: ["trip-dashboard", tripId] });
  }

  async function pause() {
    // Pause keeps the trip session alive — same tripId, same startedAt, same
    // Live Activity, same outbox. Only the GPS watcher stops.
    await pauseActiveWatch({ source: "tracking-screen-pause" });
    await setTrackingPaused(true);
  }

  async function resume() {
    // Resume re-arms the watcher with the same tripId. No new session, no
    // duplicate trip, no preset-change suppression.
    await resumeActiveWatch({ source: "tracking-screen-resume" });
    await setTrackingPaused(false);
  }

  // Brief brand-blue pulse on the Route tracking card after a successful GPS
  // preset change. Subtle, no layout shift, respects prefers-reduced-motion.
  // Uses a nonce + ref-tracked timeout so repeated successful changes retrigger
  // the animation reliably instead of being swallowed by an in-flight timer.
  const [presetPulse, setPresetPulse] = useState(0);
  const presetPulseTimer = useRef<number | null>(null);
  useEffect(() => {
    function onPresetChanged() {
      if (presetPulseTimer.current != null) {
        window.clearTimeout(presetPulseTimer.current);
        presetPulseTimer.current = null;
      }
      // Force restart by toggling off first.
      setPresetPulse(0);
      window.requestAnimationFrame(() => {
        setPresetPulse((n) => n + 1);
        presetPulseTimer.current = window.setTimeout(() => {
          setPresetPulse(0);
          presetPulseTimer.current = null;
        }, 1400);
      });
    }
    window.addEventListener("roava:gps:preset-changed", onPresetChanged);
    return () => {
      window.removeEventListener("roava:gps:preset-changed", onPresetChanged);
      if (presetPulseTimer.current != null) window.clearTimeout(presetPulseTimer.current);
    };
  }, []);




  useBatteryGuard({ tracking, startedAt, stop });

  const authLevelRef = useRef(authLevel);
  useEffect(() => { authLevelRef.current = authLevel; }, [authLevel]);

  async function refreshAuthLevel() {
    try {
      const level = await getLocationAuthLevel();
      const prev = authLevelRef.current;
      setAuthLevel(level);
      if (level === "always") {
        setGuidance("idle");
        setGuidanceMessage(t("tracking.guidance_bg_enabled"));
        if (prev !== "always" && prev !== "unknown") {
          toast.success(t("tracking.toast_bg_enabled"));
        }
      }
      return level;
    } catch {
      return authLevelRef.current;
    }
  }

  const pollTimerRef = useRef<number | null>(null);
  function pollForAlways() {
    if (pollTimerRef.current) return;
    const started = Date.now();
    pollTimerRef.current = window.setInterval(async () => {
      const level = await refreshAuthLevel();
      if (level === "always" || Date.now() - started > 30_000) {
        if (pollTimerRef.current) {
          clearInterval(pollTimerRef.current);
          pollTimerRef.current = null;
        }
      }
    }, 2000);
  }

  async function upgradeToAlways() {
    try {
      const res = await LocationBG.requestAlways();
      const next = res.status === "restricted" ? "denied" : res.status;
      setAuthLevel(next);
      if (next !== "always") {
        setGuidance("open-settings");
        setGuidanceMessage(t("tracking.guidance_open_choose"));
        pollForAlways();
      }
    } catch {
      setGuidance("open-settings");
      setGuidanceMessage(t("tracking.guidance_open_choose"));
      pollForAlways();
    }
  }

  async function openAppSettings() {
    if (!isNative()) return;
    setGuidanceMessage(t("tracking.guidance_opening"));
    try {
      window.location.href = "app-settings:";
    } catch { /* ignore */ }
  }

  async function confirmAlwaysEnabled() {
    const level = await refreshAuthLevel();
    if (level === "always") {
      setGuidance("idle");
      setGuidanceMessage(t("tracking.guidance_bg_enabled"));
      toast.success(t("tracking.toast_bg_enabled"));
      return;
    }
    if (level === "denied") {
      setGuidanceMessage(t("tracking.guidance_blocked"));
      toast.error(t("tracking.toast_bg_blocked"));
      return;
    }
    // "whenInUse", "prompt", "unknown" — do NOT flip state to "always" or
    // claim success. Leave the banner up so the user can try again.
    setGuidanceMessage(t("tracking.guidance_still_needs"));
    toast.error(t("tracking.toast_still_while"));
  }

  useEffect(() => {
    void refreshAuthLevel();
    if (tripId) void drainNativeLocationQueue(tripId);
    if (isNative()) {
      void probeLocationBG().then((r) => {
        setPluginError(r.errorMessage ?? null);
        setPluginStatus(
          r.state === "registered_ok" ? "installed"
          : r.state === "registered_errored" ? "errored"
          : "missing"
        );
      });
      void Promise.all([
        probePlugin("LocationBG", () => LocationBG.getAuthStatus(), "getAuthStatus()"),
        probePlugin("PhotoScan", () => PhotoScan.requestPermission(), "requestPermission()"),
        probePlugin("NativeBiometric", async () => {
          const { NativeBiometric } = await import("@capgo/capacitor-native-biometric");
          return NativeBiometric.isAvailable();
        }, "isAvailable()"),
      ]).then(setInventory);
    } else {
      setPluginStatus("missing");
    }

    const onVisible = () => {
      if (document.visibilityState === "visible") void refreshAuthLevel();
    };
    const onFocus = () => { void refreshAuthLevel(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);

    let removeAppListener: (() => void) | null = null;
    if (isNative()) {
      void import("@capacitor/app").then(({ App }) => {
        App.addListener("appStateChange", ({ isActive }) => {
          if (isActive) void refreshAuthLevel();
        }).then((handle) => {
          removeAppListener = () => { void handle.remove(); };
        });
      }).catch(() => {});
    }

    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
      if (removeAppListener) removeAppListener();
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const diagFn = useServerFn(upsertMyDiagnostics);
  useEffect(() => {
    if (!tripId) return;
    if (!isNative() && inventory.length === 0) return;
    let cancelled = false;
    (async () => {
      let appVersion: string | null = null;
      let appBuild: string | null = null;
      if (isNative()) {
        try {
          const { App } = await import("@capacitor/app");
          const info = await App.getInfo();
          appVersion = info.version ?? null;
          appBuild = info.build ?? null;
        } catch { /* ignore */ }
      }
      if (cancelled) return;

      // Best-effort device snapshot — never throws.
      let snap: Awaited<ReturnType<typeof import("@/lib/native/device-snapshot").collectDeviceSnapshot>> | null = null;
      try {
        const mod = await import("@/lib/native/device-snapshot");
        snap = await mod.collectDeviceSnapshot(
          inventory.map((p) => ({ name: p.name, state: p.state })),
        );
      } catch { /* non-fatal */ }
      if (cancelled) return;

      try {
        await diagFn({
          data: {
            tripId,
            pluginInventory: inventory.map((p) => ({
              name: p.name,
              state: p.state,
              probedMethod: p.probedMethod ?? null,
            })),
            appVersion,
            appBuild,
            otaBuildId: BUILD_ID,
            locationAuthLevel: authLevel,
            platform: snap?.platform ?? null,
            deviceModel: snap?.deviceModel ?? null,
            osVersion: snap?.osVersion ?? null,
            batteryLevel: snap?.batteryLevel ?? null,
            batteryState: snap?.batteryState ?? null,
            lowPowerMode: snap?.lowPowerMode ?? null,
            networkType: snap?.networkType ?? null,
            pushRegistered: snap?.pushRegistered ?? null,
            liveActivityState: snap?.liveActivityState ?? null,
            healthkitState: snap?.healthkitState ?? null,
            shellMode: snap?.shellMode ?? null,
            lastShellError: snap?.lastShellError ?? null,
            nativeQueueCount: snap?.nativeQueueCount ?? null,
            lastNativeLocationAt: snap?.lastNativeLocationAt ?? null,
          },
        });
      } catch { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [tripId, inventory, authLevel, diagFn]);

  useEffect(() => {
    if (!isNative()) return;
    if (!tripId) return;
    if (!tracking) return;
    if (isActiveWatchRunning()) return;
    void startActiveWatch(tripId);
  }, [tripId, tracking]);

  useEffect(() => {
    if (!tracking) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [tracking]);

  // Refetch route/track/media as soon as a sync drain completes, so the
  // visible counters update immediately instead of waiting for the 15s tick.
  useEffect(() => {
    if (!tripId) return;
    const onDrain = () => {
      refetch();
      refetchTrack();
      qc.invalidateQueries({ queryKey: ["my-media", tripId] });
    };
    window.addEventListener("mtsync:drain-complete", onDrain);
    return () => window.removeEventListener("mtsync:drain-complete", onDrain);
  }, [tripId, refetch, refetchTrack, qc]);


  const lastCaptured = stats?.last?.captured_at ? new Date(stats.last.captured_at).getTime() : null;
  const sinceLast = lastCaptured ? Math.max(0, Math.floor((now - lastCaptured) / 1000)) : null;
  const statsUnknown = !stats && (statsLoading || statsError);
  const fmtAge = (secs: number) =>
    secs < 60
      ? t("tracking.age_s_ago", { n: secs })
      : secs < 3600
        ? t("tracking.age_m_ago", { n: Math.floor(secs / 60) })
        : t("tracking.age_h_ago", { n: Math.floor(secs / 3600) });
  const sinceLabel = sinceLast == null
    ? (statsUnknown ? t("tracking.since_checking") : t("tracking.since_waiting"))
    : fmtAge(sinceLast);
  const countLabel = stats ? `${stats.count ?? 0}` : (statsUnknown ? "—" : "0");


  const showAlwaysBanner = tracking && isNative() && authLevel === "whenInUse";

  // StatusChip for the main Route tracking card
  const permissionNeeded =
    isNative() && (authLevel === "denied" || authLevel === "whenInUse" || authLevel === "prompt");
  const mainChip = tracking
    ? { variant: "success" as const, label: t("tracking.chip_live") }
    : permissionNeeded
      ? { variant: "warning" as const, label: t("tracking.chip_permission_needed") }
      : { variant: "neutral" as const, label: t("tracking.chip_idle") };

  const startedLabel = startedAt ? new Date(startedAt).toLocaleTimeString() : "—";

  return (
    <>
      <RoavaPage
        title={t("tracking.page_title")}
        subtitle={t("tracking.page_subtitle")}
        action={<TripSwitcher />}
      >
        <FlightModeTip />

        {/* Sync now — visible on the home tab so travelers can drain the queue without hunting. */}
        <SyncOutboxCard />

        {/* Main Route tracking card */}

        {tracking && isNative() && authLevel === "whenInUse" && (
          <RoavaCard
            icon={<AlertTriangle className="size-5" />}
            iconTone="orange"
            title="Background tracking is limited"
            description="Roava only records while the app is open. To keep recording when your screen is locked, enable Always for Location in iOS Settings."
            className="mb-3"
          >
            <div className="grid gap-2">
              <SecondaryButton
                onClick={async () => {
                  try {
                    await openAppSettings();
                  } catch {
                    toast.message("Open iOS Settings → Privacy → Location Services → Roava, then choose Always.");
                  }
                }}
                className="h-12 w-full rounded-2xl"
              >
                <ExternalLink className="size-4 mr-2" />
                Open Settings
              </SecondaryButton>
              <p className="text-[11px] leading-snug text-[var(--roava-text-muted)] px-1">
                In Settings → Privacy → Location Services → Roava, choose Always.
              </p>
            </div>
          </RoavaCard>
        )}

        <RoavaCard
          icon={<MapPin className="size-5" />}
          iconTone={tracking ? "green" : "gray"}
          title={
            <div className="flex items-center justify-between gap-2">
              <span>{t("tracking.card_title")}</span>
              <StatusChip variant={mainChip.variant}>{mainChip.label}</StatusChip>
            </div>
          }
          description={
            tracking
              ? t("tracking.desc_points_with_age", { count: countLabel, age: sinceLabel })
              : lastCaptured
                ? t("tracking.desc_last_point_at", { time: new Date(lastCaptured).toLocaleTimeString() })
                : statsUnknown
                  ? t("tracking.desc_checking")
                  : t("tracking.desc_start_to_begin")
          }
          className={`mb-3 ${presetPulse > 0 ? "roava-pulse-brand" : ""}`}
        >
          {trackingPhase !== "off" ? (
            <>
              <div className="mb-4">
                {tripName && <SettingRow label={t("tracking.row_trip")} control={<span className="text-sm text-[var(--roava-text)]">{tripName}</span>} />}
                <SettingRow label={t("tracking.row_started")} control={<span className="text-sm text-[var(--roava-text)]">{startedLabel}</span>} />
                <SettingRow
                  label={t("tracking.row_last_gps")}
                  control={(() => {
                    const ts = gps.lastFixAt;
                    if (!ts) return <span className="text-sm text-[var(--roava-text-muted)]">{t("tracking.val_waiting")}</span>;
                    const s = Math.max(0, Math.floor((now - ts) / 1000));
                    return <span className="text-sm text-[var(--roava-text)]">{fmtAge(s)}</span>;
                  })()}
                />
                <SettingRow label={t("tracking.row_route_points")} control={<span className="text-sm text-[var(--roava-text)] tabular-nums">{countLabel}</span>} />
                {trackingPhase === "paused" && (
                  <SettingRow
                    label="Status"
                    control={<span className="text-sm font-medium text-[var(--roava-orange)]">Paused</span>}
                  />
                )}
              </div>

              {/* Primary control. trackingPhase drives the label/action:
                  active  → Pause tracking (secondary)
                  paused  → Resume tracking (primary) + Stop tracking (destructive, separated) */}
              <div className="grid gap-3">
                {trackingPhase === "paused" ? (
                  <PrimaryButton
                    onClick={resume}
                    className="h-14 w-full rounded-2xl text-base font-semibold"
                  >
                    <Play className="size-5 mr-2" />
                    Resume tracking
                  </PrimaryButton>
                ) : (
                  <SecondaryButton
                    onClick={pause}
                    className="h-14 w-full rounded-2xl text-base font-semibold"
                  >
                    <Pause className="size-5 mr-2" />
                    Pause tracking
                  </SecondaryButton>
                )}

                {tripId && (
                  <ManualGpsButton tripId={tripId} onSuccess={() => { refetch(); refetchTrack(); }} />
                )}

                {/* Separated destructive action. Stop ends this trip session. */}
                <div className="mt-2 pt-3 border-t border-[var(--roava-border)] grid gap-1.5">
                  <DangerButton
                    onClick={stop}
                    className="h-12 w-full rounded-2xl text-sm font-semibold"
                  >
                    <Square className="size-4 mr-2" />
                    Stop tracking
                  </DangerButton>
                  <p className="text-[11px] leading-snug text-[var(--roava-text-muted)] text-center px-2">
                    Ends this tracking session. Pause keeps the session alive — Stop closes it.
                  </p>
                </div>
              </div>
            </>

          ) : (
            <div className="grid gap-2">
              <PrimaryButton
                onClick={start}
                className="h-14 w-full rounded-2xl text-base font-semibold"
              >
                <Play className="size-5 mr-2" />
                {t("tracking.btn_start")}
              </PrimaryButton>
              <SecondaryButton
                onClick={() => setGpsSheet(true)}
                className="h-12 w-full rounded-2xl"
              >
                <SettingsIcon className="size-4 mr-2" />
                {t("tracking.btn_gps_detail")}
              </SecondaryButton>
            </div>
          )}
        </RoavaCard>

        {/* When tracking, also expose GPS detail entry below the action card */}
        {tracking && (
          <div className="mb-3 flex justify-end">
            <TextButton onClick={() => setGpsSheet(true)} className="text-sm">
              {t("tracking.link_gps_detail")}
            </TextButton>
          </div>
        )}

        <OfflineRouteStatus />

        <OfflineTrackingStatus className="mt-4" />

        <OfflineMapPanel className="mt-4" />

        <BatteryGuardCard />

        {isNative() && (() => {
          const pingAgeSec = lastCaptured ? Math.floor((now - lastCaptured) / 1000) : null;
          const pingsHealthy = pingAgeSec != null && pingAgeSec <= 60;
          const pingsRecent = pingAgeSec != null && pingAgeSec <= 300;

          // Freshness color only applies while tracking is ON. When tracking
          // is off, the newest server point legitimately ages forever — red
          // would be alarmist. Keep it neutral.
          const recencyVariant = !tracking
            ? ("neutral" as const)
            : pingsHealthy
              ? ("success" as const)
              : pingsRecent
                ? ("warning" as const)
                : pingAgeSec == null
                  ? ("neutral" as const)
                  : ("danger" as const);
          const ageLabel = pingAgeSec == null
            ? (tracking ? t("tracking.val_waiting") : t("tracking.val_dash"))
            : fmtAge(pingAgeSec);
          const countText = (stats?.count ?? 0).toLocaleString();

          const permVariant =
            authLevel === "always" ? ("success" as const) :
            authLevel === "denied" ? ("danger" as const) :
            authLevel === "whenInUse" ? ("warning" as const) :
            ("neutral" as const);
          const permLabel =
            authLevel === "always" ? t("tracking.perm_always") :
            authLevel === "whenInUse" ? t("tracking.perm_while_use") :
            authLevel === "denied" ? t("tracking.perm_denied") :
            authLevel === "prompt" ? t("tracking.perm_not_set") : t("tracking.perm_unknown");

          const watcherVariant =
            pluginStatus === "installed" ? ("success" as const) :
            pluginStatus === "errored" ? ("danger" as const) :
            pluginStatus === "missing" ? ("warning" as const) :
            ("neutral" as const);
          const watcherLabel =
            pluginStatus === "installed" ? t("tracking.watcher_installed") :
            pluginStatus === "errored" ? t("tracking.watcher_errored") :
            pluginStatus === "missing" ? t("tracking.watcher_missing") :
            t("tracking.watcher_checking");

          return (
            <RoavaCard title={t("tracking.diag_title")} className="mt-4">
              <div>
                <SettingRow
                  label={t("tracking.diag_location_perm")}
                  control={<StatusChip variant={permVariant}>{permLabel}</StatusChip>}
                />
                <SettingRow
                  label={t("tracking.diag_bg_watcher")}
                  description={pluginError ?? undefined}
                  control={<StatusChip variant={watcherVariant}>{watcherLabel}</StatusChip>}
                />
                <SettingRow
                  label={t("tracking.diag_last_synced")}
                  description={
                    tracking
                      ? t("tracking.diag_last_desc_tracking")
                      : t("tracking.diag_last_desc_off")
                  }
                  control={
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium tabular-nums text-[var(--roava-text-muted)]">{countText}</span>
                      <StatusChip variant={recencyVariant}>{ageLabel}</StatusChip>
                    </div>
                  }
                />
              </div>


              {pluginStatus === "installed" && authLevel !== "always" && authLevel !== "unknown" && (
                <RoavaCard
                  className="mt-4 border-[var(--roava-orange)]/30 bg-[var(--roava-orange)]/8"
                  title={
                    authLevel === "prompt"
                      ? t("tracking.banner_prompt_title")
                      : authLevel === "denied"
                        ? t("tracking.banner_denied_title")
                        : t("tracking.banner_always_title")
                  }
                  description={
                    authLevel === "prompt"
                      ? t("tracking.banner_prompt_desc")
                      : authLevel === "denied"
                        ? t("tracking.banner_denied_desc")
                        : t("tracking.banner_always_desc")
                  }
                >
                  {authLevel === "prompt" ? (
                    <PrimaryButton onClick={upgradeToAlways} className="h-10 w-full rounded-xl">
                      <MapPin className="size-3.5 mr-2" /> {t("tracking.btn_request_perm")}
                    </PrimaryButton>
                  ) : (
                    <PrimaryButton onClick={openAppSettings} className="h-10 w-full rounded-xl">
                      <ExternalLink className="size-3.5 mr-2" /> {t("tracking.btn_open_settings")}
                    </PrimaryButton>
                  )}
                </RoavaCard>
              )}
            </RoavaCard>
          );
        })()}

        {showAlwaysBanner && (
          <RoavaCard
            className="mt-4 border-[var(--roava-orange)]/30 bg-[var(--roava-orange)]/8"
            icon={
              guidance === "open-settings"
                ? <SettingsIcon className="size-5" />
                : <MapPin className="size-5" />
            }
            iconTone="orange"
            title={guidance === "open-settings" ? t("tracking.always_banner_finish_title") : t("tracking.always_banner_keep_title")}
            description={
              guidance === "open-settings"
                ? t("tracking.always_banner_finish_desc")
                : t("tracking.always_banner_keep_desc")
            }
          >
            {guidance === "open-settings" && (
              <ol className="mb-4 space-y-2.5 rounded-2xl bg-[var(--roava-gray)] p-4">
                {[
                  { n: 1, key: "tracking.step1" },
                  { n: 2, key: "tracking.step2" },
                  { n: 3, key: "tracking.step3", done: true },
                ].map((s) => (
                  <li key={s.n} className="flex items-center gap-3 text-[14px] text-[var(--roava-text-muted)]">
                    <span className="size-6 rounded-full bg-[var(--roava-orange)]/15 text-[var(--roava-orange)] grid place-items-center text-[12px] font-semibold shrink-0">
                      {s.done ? <Check className="size-3.5" strokeWidth={2.5} /> : s.n}
                    </span>
                    <span><Trans i18nKey={s.key} components={{ bold: <span className="font-medium text-[var(--roava-text)]" /> }} /></span>
                  </li>
                ))}
              </ol>
            )}

            {guidance === "open-settings" ? (
              <div className="flex flex-col gap-2">
                <PrimaryButton onClick={openAppSettings} className="w-full h-12 rounded-2xl">
                  <ExternalLink className="size-4 mr-2" />
                  {t("tracking.btn_open_settings")}
                </PrimaryButton>
                <TextButton tone="muted" onClick={() => void confirmAlwaysEnabled()} className="text-[13px] mx-auto">
                  {t("tracking.btn_ive_enabled")}
                </TextButton>
                <p className="text-[12px] text-[var(--roava-text-muted)]/80 text-center">
                  {guidanceMessage}
                </p>
              </div>
            ) : (
              <PrimaryButton onClick={upgradeToAlways} className="w-full h-12 rounded-2xl">
                {t("tracking.btn_enable_always")}
              </PrimaryButton>
            )}
          </RoavaCard>
        )}

        <div className="mt-4" data-tour="tracking.map">
          <MapErrorBoundary>
            <RouteMap points={track?.points ?? []} live={tracking} photos={photoMarkers} />
          </MapErrorBoundary>
        </div>
        {(track?.points?.length ?? 0) > 0 && (
          <p className="mt-2 text-xs text-[var(--roava-text-muted)] leading-relaxed">
            {t("tracking.map_tap_hint")}
          </p>
        )}
        {isNative() && authLevel === "always" && (
          <p className="mt-4 text-xs text-[var(--roava-text-muted)] leading-relaxed">
            {t("tracking.foot_always_on")}
          </p>
        )}
        {isNative() && authLevel === "whenInUse" && (
          <p className="mt-4 text-xs text-[var(--roava-text-muted)] leading-relaxed">
            {t("tracking.foot_when_in_use")}
          </p>
        )}
        {!isNative() && (
          <p className="mt-4 text-xs text-[var(--roava-text-muted)] leading-relaxed">
            {t("tracking.foot_web_preview")}
          </p>
        )}
      </RoavaPage>

      <GpsRateSheet open={gpsSheet} onOpenChange={setGpsSheet} />

      <AlertDialog
        open={lowBatteryConfirm !== null}
        onOpenChange={(open) => { if (!open) setLowBatteryConfirm(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("tracking.lb_title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {lowBatteryConfirm
                ? t("tracking.lb_body", { pct: lowBatteryConfirm.pct, threshold: lowBatteryConfirm.threshold })
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col gap-2">
            <AlertDialogCancel onClick={() => setLowBatteryConfirm(null)}>
              {t("tracking.lb_not_now")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setLowBatteryConfirm(null);
                void performStart();
              }}
            >
              {t("tracking.lb_start")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

